import { detectFlowsheetType } from '../../Designer/services/flowsheet-templates';

/**
 * AI Chat Service - Connects to local Ollama instance
 *
 * To use:
 * 1. Install Ollama: curl -fsSL https://ollama.com/install.sh | sh
 * 2. Pull a model: ollama pull llama3:8b (or mistral, qwen2.5, etc.)
 * 3. Run Ollama: ollama serve
 * 4. The service will connect to http://localhost:11434
 */

const OLLAMA_URL = 'http://localhost:11434/api/generate';
const MODEL = 'llama3.1:8b';

const SYSTEM_PROMPT = `You are FlowWire AI, a chemical process simulation assistant. Be brief and direct - max 2-3 sentences unless more detail is needed.

You can see the diagram data below. When asked to:
- Run a simulation: say "Running simulation now."
- Open economics: say "Opening economics now."
- Add equipment: say "Adding [equipment type] now." where equipment type is one of: pump, vessel, feed, heat exchanger, mixer, splitter, crusher, filter, settler, agitated vessel, cyclone, screen, flotation, thickener, dryer, kiln, crystallizer, evaporator, distillation column, absorption column, reactor, flash drum.
- Build a flowsheet: say "Building [name] flowsheet now." where name is one of: wastewater treatment, mineral processing, thickening circuit, leaching circuit, distillation.
- Build a CUSTOM flowsheet: output ONLY a JSON object with no other text. Layout rules: col increases left-to-right (main flow), row=0 for main flow, row=1 for side branches. Valid unit types: Feed, Pump, Vessel, HeatExchanger, MixerSplitter, Crusher, Filter, Settler, AgitatedVessel, Cyclone, Screen, Flotation, Thickener, Reactor, FlashDrum, DistillationColumn, AbsorptionColumn, Dryer, Kiln, Crystallizer, Evaporator.
Example custom output: {"name":"Leaching","units":[{"type":"Feed","label":"Ore Feed","col":0,"row":0},{"type":"Reactor","label":"Leach Tank","col":1,"row":0},{"type":"Settler","label":"CCD","col":2,"row":0},{"type":"Filter","label":"Filter","col":3,"row":0}],"connections":[[0,1],[1,2],[2,3]]}

Answer questions about the diagram, equipment, streams, and chemical engineering. Be specific with numbers when available.`;

const EQUIPMENT_TYPES = {
    'pump': 'Pump',
    'vessel': 'Vessel',
    'feed': 'Feed',
    'heat exchanger': 'HeatExchanger',
    'heater': 'HeatExchanger',
    'cooler': 'HeatExchanger',
    'mixer': 'MixerSplitter',
    'splitter': 'MixerSplitter',
    'crusher': 'Crusher',
    'filter': 'Filter',
    'settler': 'Settler',
    'agitated vessel': 'AgitatedVessel',
    'agitated tank': 'AgitatedVessel',
    'cyclone': 'Cyclone',
    'screen': 'Screen',
    'flotation': 'Flotation',
    'thickener': 'Thickener',
    'dryer': 'Dryer',
    'kiln': 'Kiln',
    'crystallizer': 'Crystallizer',
    'evaporator': 'Evaporator',
    'distillation column': 'DistillationColumn',
    'distillation': 'DistillationColumn',
    'absorption column': 'AbsorptionColumn',
    'absorber': 'AbsorptionColumn',
    'reactor': 'Reactor',
    'flash drum': 'FlashDrum',
    'flash': 'FlashDrum',
    'tank': 'Vessel',
    'column': 'DistillationColumn'
};

/**
 * Tries to extract a flowsheet template JSON object from an LLM response.
 * Returns the parsed template or null.
 */
function extractTemplate(text) {
    if (!text.includes('"units"') || !text.includes('"connections"')) return null;

    // Try JSON code block first
    const blockMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (blockMatch) {
        try {
            const t = JSON.parse(blockMatch[1]);
            if (Array.isArray(t.units) && Array.isArray(t.connections)) return t;
        } catch {}
    }

    // Find first { to last } in the whole response
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end > start) {
        try {
            const t = JSON.parse(text.slice(start, end + 1));
            if (Array.isArray(t.units) && Array.isArray(t.connections)) return t;
        } catch {}
    }

    return null;
}

export const sendChatMessage = async (message, conversationHistory, diagramContext) => {
    // eslint-disable-next-line no-unreachable
    return { response: "Sorry, I'm not available right now.", action: null };

    let contextString = '';

    if (diagramContext) {
        contextString = '\n\n=== CURRENT DIAGRAM DATA ===';
        contextString += '\nEquipment count: ' + diagramContext.equipmentCount;
        contextString += '\nStream count: ' + diagramContext.streamCount;

        if (diagramContext.equipment && diagramContext.equipment.length > 0) {
            contextString += '\n\n--- Equipment ---';
            diagramContext.equipment.forEach(eq => {
                contextString += '\n\n' + eq.label + ' (' + eq.type + ')';
                if (eq.unitProperties && Object.keys(eq.unitProperties).length > 0) {
                    contextString += '\n  Properties: ' + JSON.stringify(eq.unitProperties);
                }
                if (eq.properties && Object.keys(eq.properties).length > 0) {
                    contextString += '\n  Economics: ' + JSON.stringify(eq.properties);
                }
            });
        }

        if (diagramContext.streams && diagramContext.streams.length > 0) {
            contextString += '\n\n--- Streams ---';
            diagramContext.streams.forEach(stream => {
                contextString += '\n\nStream: ' + stream.name;
                if (stream.solvedProperties && Object.keys(stream.solvedProperties).length > 0) {
                    const solved = stream.solvedProperties;
                    if (solved.totalFlow !== undefined) {
                        contextString += '\n  Total Flow: ' + solved.totalFlow;
                    }
                    if (solved.massFlow) {
                        contextString += '\n  Mass Flow: ' + JSON.stringify(solved.massFlow);
                    }
                }
                if (stream.streamProperties && Object.keys(stream.streamProperties).length > 0) {
                    const spec = stream.streamProperties;
                    if (spec.totalFlow !== undefined) {
                        contextString += '\n  Specified Total Flow: ' + spec.totalFlow;
                    }
                    if (spec.massFlow) {
                        contextString += '\n  Specified Mass Flow: ' + JSON.stringify(spec.massFlow);
                    }
                }
            });
        }

        if (diagramContext.economics) {
            const econ = diagramContext.economics;
            const fmt = (val) => (val !== null && val !== undefined) ? Number(val).toFixed(2) : 'N/A';
            contextString += '\n\n--- Economic Parameters ---';
            contextString += '\nProject Life: ' + (econ.years ?? 'N/A') + ' years';
            contextString += '\nInterest Rate: ' + (econ.interestRate !== null && econ.interestRate !== undefined ? (econ.interestRate * 100).toFixed(1) : 'N/A') + '%';
            contextString += '\nElectricity Cost: $' + fmt(econ.elecCost) + '/MWh';
            contextString += '\nNPV: $' + fmt(econ.npvCalculated);
            contextString += '\nLCOP: $' + fmt(econ.LCOP);
            contextString += '\nTotal CAPEX Installed: $' + fmt(econ.totalCapexInstalled);
            contextString += '\nEquipment CAPEX: $' + fmt(econ.equipmentCapex);
            contextString += '\nTotal OPEX (Annual): $' + fmt(econ.totalOpexDisplay);
            contextString += '\nAnnual Product Revenue: $' + fmt(econ.productRevAnnual);
            contextString += '\nAnnual Feedstock Cost: $' + fmt(econ.feedCostSumAnnual);
            contextString += '\nUtility Costs: $' + fmt(econ.utilityCosts);
            contextString += '\nLabor Costs: $' + fmt(econ.laborCosts);
            contextString += '\nMaintenance Costs: $' + fmt(econ.maintenanceCosts);
        }

        contextString += '\n\n=== END DIAGRAM DATA ===';
    }

    const historyString = conversationHistory
        .slice(-6)
        .map(msg => (msg.role === 'user' ? 'User' : 'Assistant') + ': ' + msg.content)
        .join('\n');

    const fullPrompt = SYSTEM_PROMPT + contextString + '\n\nPrevious conversation:\n' + historyString + '\n\nUser: ' + message + '\n\nAssistant:';

    const response = await fetch(OLLAMA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            model: MODEL,
            prompt: fullPrompt,
            stream: false,
            options: { temperature: 0.7, num_predict: 400 }
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to connect to Ollama: ' + response.statusText);
    }

    const data = await response.json();
    const responseText = data.response || 'Sorry, I could not generate a response.';

    let action = null;
    const lowerResponse = responseText.toLowerCase();
    const lowerMessage = message.toLowerCase();

    // Detect build intent from user's message directly
    const BUILD_INTENT = ['build', 'add', 'create', 'make', 'show', 'generate', 'place', 'draw'];
    const hasBuildIntent = BUILD_INTENT.some(w => lowerMessage.includes(w));

    // Check for inline JSON template from LLM (custom flowsheet)
    const template = extractTemplate(responseText);
    if (template) {
        action = { type: 'build_template', template };
    } else if (hasBuildIntent && detectFlowsheetType(lowerMessage)) {
        action = { type: 'build_flowsheet', flowsheetType: detectFlowsheetType(lowerMessage) };
    } else if (lowerResponse.includes('running simulation') || lowerResponse.includes('simulate now') || lowerResponse.includes('starting simulation')) {
        action = { type: 'run_simulation' };
    } else if (lowerResponse.includes('opening economics') || lowerResponse.includes('economics now') || lowerResponse.includes('showing economics')) {
        action = { type: 'open_economics' };
    } else if (hasBuildIntent) {
        for (const [keyword, equipmentType] of Object.entries(EQUIPMENT_TYPES)) {
            if (lowerMessage.includes(keyword)) {
                action = { type: 'add_equipment', equipmentType };
                break;
            }
        }
    }

    return {
        message: responseText.trim(),
        action
    };
};

export const checkOllamaConnection = async () => {
    try {
        const response = await fetch('http://localhost:11434/api/tags');
        return response.ok;
    } catch {
        return false;
    }
};
